import { api } from './api';

export interface SystemOverview {
  users: {
    total: number;
    by_level: Array<{ total: number; user_level: string }>;
  };
  hospitals: number;
  patients: number;
  premature_cases: number;
  diagnoses: number;
  recent_activity: number;
  database_size: {
    database_name: string;
    size_mb: number;
  };
  recent_registrations: Array<{
    date: string;
    count: number;
  }>;
}

export interface SystemHealth {
  database: {
    status: 'operational' | 'degraded' | 'down';
    response_time_ms: number;
  };
  api: {
    status: 'operational' | 'degraded' | 'down';
    uptime_seconds: number;
  };
  storage: {
    used_gb: number;
    total_gb: number;
    percentage_used: number;
  };
  backup: {
    last_backup: string | null;
    next_scheduled: string;
    status: 'success' | 'failed' | 'running' | 'scheduled';
  };
}

export interface AuditLog {
  audit_id: number;
  user_id: number;
  username: string;
  full_name: string;
  action: string;
  table_name: string;
  record_id: number | null;
  old_values: any;
  new_values: any;
  created_at: string;
  action_timestamp: string;
  ip_address: string;
}

export interface AuditStatistics {
  total_actions: number;
  actions_by_type: Record<string, number>;
  most_active_users: Array<{
    username: string;
    full_name: string;
    action_count: number;
  }>;
  actions_by_table: Record<string, number>;
  actions_by_date: Array<{
    date: string;
    count: number;
  }>;
}

export interface User {
  user_id: number;
  username: string;
  full_name: string;
  email: string;
  user_level: string;
  user_level_id: number;
  hospital_id: number | null;
  hospital_name?: string;
  assigned_section?: 'chd' | 'prematuri' | null;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
}

export interface Hospital {
  id: number;
  name: string;
  code: string;
  city: string;
  county: string;
  address: string;
  phone: string;
  email: string;
  is_active: boolean;
  created_at: string;
  user_count?: number;
  patient_count?: number;
  // Legacy field names for compatibility
  hospital_id?: number;
  hospital_name?: string;
  hospital_code?: string;
}

export interface EurocatStatus {
  last_report_date: string | null;
  next_report_date: string;
  pending_cases: number;
  compliance_percentage: number;
  validation_errors: number;
}

export interface Backup {
  id: number;
  filename: string;
  size_mb: number;
  type: 'manual' | 'scheduled' | 'automatic';
  status: 'completed' | 'in_progress' | 'failed';
  created_at: string;
  created_by: string;
  tables_included: string[];
  compression: boolean;
  encrypted: boolean;
}

export interface BackupSchedule {
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  time: string;
  retention_days: number;
  next_run: string;
}

export interface DatabaseTable {
  name: string;
  label: string;
  records: number;
  size_mb: number;
}

class AdminService {
  async getSystemOverview(): Promise<SystemOverview> {
    const response = await api.get<SystemOverview>('/api/admin/overview');
    return response.data!;
  }

  async getSystemHealth(): Promise<SystemHealth> {
    const response = await api.get<SystemHealth>('/api/admin/health');
    return response.data!;
  }

  async getRecentActivity(limit: number = 10): Promise<AuditLog[]> {
    const response = await api.get<AuditLog[]>('/api/admin/audit', {
      params: { limit }
    });
    return response.data!;
  }

  async getAuditStatistics(startDate?: string, endDate?: string): Promise<AuditStatistics> {
    const response = await api.get<AuditStatistics>('/api/admin/audit/statistics', {
      params: { start_date: startDate, end_date: endDate }
    });
    return response.data!;
  }

  async getEurocatStatus(): Promise<EurocatStatus> {
    const response = await api.get<EurocatStatus>('/api/reports/eurocat/status');
    return response.data!;
  }

  async listUsers(hospitalId?: number, userLevel?: string, isActive?: boolean): Promise<User[]> {
    const response = await api.get<User[]>('/api/admin/users', {
      params: {
        hospital_id: hospitalId,
        user_level: userLevel,
        is_active: isActive
      }
    });
    return response.data!;
  }

  async createUser(userData: {
    username: string;
    password: string;
    full_name: string;
    email: string;
    user_level: string;
    hospital_id: number | null;
    assigned_section?: 'chd' | 'prematuri';
  }): Promise<{ user_id: number }> {
    const response = await api.post<{ user_id: number }>('/api/admin/users', userData);
    return response.data!;
  }

  async updateUser(userId: number, updates: Partial<User>): Promise<void> {
    await api.put(`/api/admin/users/${userId}`, updates);
  }

  async deactivateUser(userId: number): Promise<void> {
    await api.put(`/api/admin/users/${userId}/deactivate`);
  }

  async activateUser(userId: number): Promise<void> {
    await api.put(`/api/admin/users/${userId}/activate`);
  }

  async deleteUser(userId: number): Promise<void> {
    await api.delete(`/api/admin/users/${userId}`);
  }

  async listHospitals(): Promise<Hospital[]> {
    const response = await api.get<Hospital[]>('/api/admin/hospitals');
    return response.data!;
  }

  async createHospital(hospitalData: {
    hospital_name: string;
    hospital_code: string;
    city: string;
    county: string;
    address: string;
    phone: string;
    email: string;
  }): Promise<{ hospital_id: number }> {
    const response = await api.post<{ hospital_id: number }>('/api/admin/hospitals', hospitalData);
    return response.data!;
  }

  async updateHospital(hospitalId: number, updates: Partial<Hospital>): Promise<void> {
    await api.put(`/api/admin/hospitals/${hospitalId}`, updates);
  }

  async createBackup(): Promise<{ backup_file: string }> {
    const response = await api.post<{ backup_file: string }>('/api/admin/backup');
    return response.data!;
  }

  async listBackups(): Promise<Backup[]> {
    const response = await api.get<Backup[]>('/api/admin/backups');
    return response.data!;
  }

  async deleteBackup(backupId: number): Promise<void> {
    await api.delete(`/api/admin/backups/${backupId}`);
  }

  async downloadBackup(backupId: number, filename: string): Promise<void> {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8082'}/api/admin/backups/${backupId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading backup:', error);
      throw error;
    }
  }

  async getBackupSchedule(): Promise<BackupSchedule> {
    const response = await api.get<BackupSchedule>('/api/admin/backup/schedule');
    return response.data!;
  }

  async updateBackupSchedule(schedule: BackupSchedule): Promise<void> {
    await api.put('/api/admin/backup/schedule', schedule);
  }

  async getDatabaseTables(): Promise<DatabaseTable[]> {
    const response = await api.get<DatabaseTable[]>('/api/admin/backup/tables');
    return response.data!;
  }

  async getSystemConfiguration(): Promise<Record<string, any>> {
    const response = await api.get<Record<string, any>>('/api/admin/config');
    return response.data!;
  }

  async updateSystemConfiguration(config: Record<string, any>): Promise<void> {
    await api.put('/api/admin/config', config);
  }

  async checkDataIntegrity(): Promise<{
    total_issues: number;
    issues_by_type: Record<string, number>;
    details: Array<{
      type: string;
      table: string;
      description: string;
      severity: 'low' | 'medium' | 'high';
    }>;
  }> {
    const response = await api.get<{
      total_issues: number;
      issues_by_type: Record<string, number>;
      details: Array<{
        type: string;
        table: string;
        description: string;
        severity: 'low' | 'medium' | 'high';
      }>;
    }>('/api/admin/integrity');
    return response.data!;
  }
}

export const adminService = new AdminService();